import React, { useEffect, useState } from "react";
import { Loader2, Mail, Phone, Building2, Trash2, ChevronDown, ChevronUp, Send, CheckCircle2, Circle, Clock, Eye } from "lucide-react";
import { fetchContacts, patchContact, deleteContact } from "../../lib/api";

const STATUSES = [
  { id: "new", label: "Yeni", icon: Circle, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  { id: "read", label: "Okundu", icon: Eye, cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { id: "in_progress", label: "İşlemde", icon: Clock, cls: "bg-blue-100 text-blue-700 border-blue-200" },
  { id: "done", label: "Tamamlandı", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
];

const fmt = (d) => new Date(d).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });

const StatusBadge = ({ status }) => {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`} data-testid="contact-status-badge">
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const ContactRow = ({ c, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(c.note || "");
  const [savingNote, setSavingNote] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && (c.status === "new" || !c.status)) onChange(c, { status: "read" });
  };

  const saveNote = async () => {
    setSavingNote(true);
    await onChange(c, { note });
    setSavingNote(false);
  };

  return (
    <div className={`${c.status === "new" || !c.status ? "bg-amber-50/40" : "bg-white"}`} data-testid={`contact-row-${c.id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-4 p-4 text-left" data-testid={`contact-toggle-${c.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className={`truncate ${c.status === "new" || !c.status ? "font-bold text-navy-900" : "font-semibold text-navy-900"}`}>{c.name}</h3>
            {c.company && <span className="hidden sm:inline text-sm text-slate-500 truncate">· {c.company}</span>}
            <StatusBadge status={c.status || "new"} />
          </div>
          <p className="text-sm text-slate-600 mt-1 truncate">{c.message}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1.5">
            <span>{fmt(c.created_at)}</span>
            {c.service_label && <span className="rounded-full bg-slate-100 px-2 py-0.5">{c.service_label}</span>}
            <span className="uppercase">{c.language}</span>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 border-t border-slate-100 grid lg:grid-cols-3 gap-6" data-testid={`contact-detail-${c.id}`}>
          <div className="lg:col-span-2 space-y-4">
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{c.message}</p>
            <div className="flex flex-wrap gap-3 text-sm">
              <a href={`mailto:${c.email}?subject=${encodeURIComponent("AdalCrea — talebiniz hakkında")}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-navy-900 hover:border-amber-500" data-testid={`contact-mail-${c.id}`}>
                <Mail className="w-4 h-4 text-amber-500" /> {c.email}
              </a>
              {c.phone && (
                <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-navy-900 hover:border-amber-500">
                  <Phone className="w-4 h-4 text-amber-500" /> {c.phone}
                </a>
              )}
              {c.company && (
                <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600">
                  <Building2 className="w-4 h-4 text-amber-500" /> {c.company}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 flex gap-4">
              <span>Teams/SharePoint: {c.forwarded ? "iletildi" : "iletilemedi"}</span>
              <span>E-posta: {c.email_sent ? "gönderildi" : "gönderilmedi"}</span>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Durum</p>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => onChange(c, { status: s.id })}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                      (c.status || "new") === s.id ? s.cls : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                    data-testid={`contact-status-${s.id}-${c.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">İç not</p>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500 resize-none"
                placeholder="Görüşme notu, teklif durumu..."
                data-testid={`contact-note-${c.id}`}
              />
              <div className="flex items-center justify-between mt-2">
                <button onClick={saveNote} disabled={savingNote || note === (c.note || "")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-900 disabled:opacity-40" data-testid={`contact-note-save-${c.id}`}>
                  {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Notu kaydet
                </button>
                <button onClick={() => onDelete(c)} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-500" data-testid={`contact-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InboxAdmin = ({ onStatsChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    fetchContacts(filter).then(setItems).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = async (c, fields) => {
    const updated = await patchContact(c.id, fields);
    setItems((list) => list.map((x) => (x.id === c.id ? updated : x)));
    onStatsChange?.();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`"${c.name}" talebi silinsin mi?`)) return;
    await deleteContact(c.id);
    setItems((list) => list.filter((x) => x.id !== c.id));
    onStatsChange?.();
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: items.filter((i) => (i.status || "new") === s.id).length }), {});

  return (
    <div data-testid="inbox-admin">
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ id: "all", label: "Tümü" }, ...STATUSES].map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === s.id ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`inbox-filter-${s.id}`}
          >
            {s.label}{filter === "all" && s.id !== "all" && counts[s.id] ? ` (${counts[s.id]})` : ""}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {items.map((c) => <ContactRow key={c.id} c={c} onChange={handleChange} onDelete={handleDelete} />)}
          {items.length === 0 && <p className="p-8 text-center text-slate-400">Bu filtrede talep yok.</p>}
        </div>
      )}
    </div>
  );
};

export default InboxAdmin;
